import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Shell from "../components/app/Shell";
import AvatarUpload from "../components/app/AvatarUpload";
import LanguageSelector from "../components/app/LanguageSelector";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, EyeOff, Trash2, ChevronRight, Shield, Globe, KeyRound } from "lucide-react";
import ChangePasswordForm from "../components/app/ChangePasswordForm";

export function SettingsPage() {
  const { user, setUser, logout, changeLanguage } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [form, setForm] = useState({ display_name: "", real_name: "", hometown: "", city: "", mode_default: "anonymous", gender_icon: "neutral", current_occupation: "", career_interests: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      display_name: user.display_name || "",
      real_name: user.real_name || "",
      hometown: user.hometown || "",
      city: user.city || "NYC",
      mode_default: user.mode_default || "anonymous",
      gender_icon: user.gender_icon || "neutral",
      current_occupation: user.current_occupation || "",
      career_interests: Array.isArray(user.career_interests) ? user.career_interests : [],
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", form);
      setUser(data.user);
      toast.success(t("settings.saved"));
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const delAccount = async () => {
    if (!window.confirm("Wipe profile, presence, matches, messages, vault? Cannot undo.")) return;
    await api.delete("/profile");
    logout();
    nav("/");
  };

  if (!user) return null;

  return (
    <Shell>
      <p className="font-mono-accent text-[#E60000]">{t("settings.account")}</p>
      <h1 className="font-display text-3xl mt-2 tracking-tighter">{t("settings.title")}</h1>
      {user.age !== null && user.age !== undefined && (
        <p className="text-xs text-zinc-500 mt-2 font-mono-accent" data-testid="s-age-line">AGE {user.age} · shown only when you reveal to a match</p>
      )}

      {/* Language — change the interface language at any time */}
      <div className="mt-6 glass rounded-2xl p-5" data-testid="settings-language">
        <div className="flex items-center gap-2 mb-1">
          <Globe size={18} className="text-[#E60000]" />
          <h3 className="font-semibold">{t("language.settingsLabel")}</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-3">{t("language.settingsDesc")}</p>
        <LanguageSelector
          value={user.preferred_language || "en"}
          onChange={changeLanguage}
        />
      </div>

      <form onSubmit={save} className="mt-6 space-y-3 glass rounded-2xl p-5" data-testid="settings-form">
        <div className="pb-3 border-b border-white/5">
          <p className="text-xs font-mono-accent text-zinc-500 mb-3 tracking-wider">PROFILE PICTURE</p>
          <AvatarUpload user={user} onUpdated={setUser} />
          <p className="text-[10px] text-zinc-600 mt-3 font-mono-accent tracking-wider">
            {user.mode_default === "real"
              ? "VISIBLE MODE · YOUR PHOTO IS PUBLIC IN NEARBY SCAN"
              : "GHOST MODE · YOUR PHOTO STAYS HIDDEN UNTIL YOU REVEAL TO A MATCH"}
          </p>
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">GHOST ALIAS · PUBLIC</label>
          <input data-testid="s-display-name" value={form.display_name} onChange={e=>setForm({...form,display_name:e.target.value})} placeholder="Ghost alias" className="auth-input" />
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">REAL NAME · SHOWN IN VISIBLE MODE OR ON REVEAL</label>
          <input data-testid="s-real-name" value={form.real_name} onChange={e=>setForm({...form,real_name:e.target.value})} placeholder="Real name" className="auth-input" />
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">HOMETOWN · SHOWN ON REVEAL</label>
          <input data-testid="s-hometown" value={form.hometown} onChange={e=>setForm({...form,hometown:e.target.value})} placeholder="Hometown (optional)" className="auth-input" />
        </div>
        <select data-testid="s-mode" value={form.mode_default} onChange={e=>setForm({...form,mode_default:e.target.value})} className="auth-input">
          <option value="anonymous">Ghost mode (alias only)</option>
          <option value="real">Visible mode (real name + photo public)</option>
        </select>
        <select data-testid="s-city" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className="auth-input">
          {["NYC","LA","Miami","Austin","Chicago","Other"].map(c=><option key={c}>{c}</option>)}
        </select>
        <div className="grid grid-cols-3 gap-2">
          {["male","female","neutral"].map(g=>(
            <button type="button" key={g} onClick={()=>setForm({...form,gender_icon:g})}
              className={`py-3 rounded-xl border text-sm capitalize ${form.gender_icon===g?"border-[#E60000] bg-[#E60000]/10":"border-white/10"}`}>{g}</button>
          ))}
        </div>

        {/* OPPORTUNITY personalization */}
        <div className="pt-4 mt-2 border-t border-white/10">
          <p className="text-xs font-mono-accent text-zinc-500 mb-3 tracking-wider">CAREER · OPPORTUNITY MODE ONLY</p>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">WHAT YOU DO · OPTIONAL</label>
          <input
            data-testid="s-current-occupation"
            value={form.current_occupation}
            maxLength={80}
            onChange={e=>setForm({...form,current_occupation:e.target.value})}
            placeholder="e.g. bartender, software engineer, nurse"
            className="auth-input"
          />
          <p className="text-[10px] text-zinc-600 mt-1 font-mono-accent tracking-wider">
            USED ONLY TO RANK OPPORTUNITIES — NEVER SHOWN PUBLICLY, NEVER ASKS ABOUT SALARY
          </p>
          <label className="block text-xs font-mono-accent text-zinc-500 mt-3 mb-1 tracking-wider">
            INTERESTED CAREER CATEGORIES · OPTIONAL
          </label>
          <div className="grid grid-cols-2 gap-2" data-testid="s-career-interests">
            {[
              ["hospitality","Hospitality"],["healthcare","Healthcare"],
              ["business_management","Business / Mgmt"],["finance","Finance"],
              ["technology","Technology"],["sales","Sales"],
              ["marketing","Marketing"],["real_estate","Real Estate / Property"],
              ["legal","Legal"],["skilled_trades","Skilled Trades"],
              ["retail","Retail"],["food_beverage","Food & Beverage"],
              ["other","Other"],
            ].map(([val,label]) => {
              const on = form.career_interests.includes(val);
              return (
                <button
                  key={val}
                  type="button"
                  data-testid={`s-interest-${val}`}
                  onClick={()=>setForm({
                    ...form,
                    career_interests: on
                      ? form.career_interests.filter(x=>x!==val)
                      : [...form.career_interests, val],
                  })}
                  className={`py-2 px-3 rounded-xl border text-xs text-left transition ${
                    on ? "border-[#E60000] bg-[#E60000]/10 text-white"
                       : "border-white/10 text-zinc-300"
                  }`}>{label}</button>
              );
            })}
          </div>
        </div>

        <button data-testid="s-save" disabled={busy} className="auth-btn">{busy?t("common.loading"):t("settings.save")}</button>
      </form>

      <div className="mt-6 space-y-2">
        <div className="glass rounded-2xl p-5" data-testid="settings-security">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={18} className="text-[#E60000]" />
            <h3 className="font-semibold">{t("security.title")}</h3>
          </div>
          <p className="text-xs text-zinc-500 mb-4">{t("security.desc")}</p>
          <ChangePasswordForm />
        </div>
        <Link to="/settings/privacy" data-testid="link-privacy" className="glass rounded-2xl p-4 flex items-center gap-3">
          <Shield size={18} className="text-[#E60000]" />
          <div className="flex-1"><div className="font-semibold text-sm">{t("settings.privacyTitle")}</div><div className="text-xs text-zinc-500">{t("settings.privacyDesc")}</div></div>
          <ChevronRight size={16} className="text-zinc-500" />
        </Link>
        <button onClick={delAccount} data-testid="delete-account" className="w-full glass rounded-2xl p-4 flex items-center gap-3 text-left">
          <Trash2 size={18} className="text-[#FF1A1A]" />
          <div className="flex-1"><div className="font-semibold text-sm">{t("settings.deleteAccount")}</div><div className="text-xs text-zinc-500">{t("settings.deleteDesc")}</div></div>
        </button>
      </div>

      <style>{`
        .auth-input { width:100%; background:rgba(0,0,0,0.5); border:1px solid rgba(255,255,255,0.1); border-radius: 12px; padding:12px 14px; color:white; }
        .auth-input:focus { outline:none; border-color:#E60000; }
        .auth-btn { width:100%; background:#E60000; color:white; font-weight:600; padding:12px; border-radius:9999px; }
        .auth-btn:hover { background:#FF1A1A; }
        .auth-btn:disabled { opacity:0.5; }
      `}</style>
    </Shell>
  );
}

export function PrivacyPage() {
  const { user, setUser } = useAuth();
  const [code, setCode] = useState(user?.disguise_code || "=blink");
  const [enabled, setEnabled] = useState(!!user?.disguise_enabled);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/blocks");
      setBlocks(data.blocks);
    })();
  }, []);

  const save = async () => {
    try {
      const { data } = await api.patch("/profile", { disguise_enabled: enabled, disguise_code: code });
      setUser(data.user);
      toast.success("Saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const unblock = async (id) => {
    await api.delete(`/blocks/${id}`);
    setBlocks(blocks.filter(b=>b.id!==id));
  };

  return (
    <Shell>
      <p className="font-mono-accent text-[#E60000]">Privacy</p>
      <h1 className="font-display text-3xl mt-2 tracking-tighter">Privacy & Disguise</h1>

      <div className="mt-6 glass rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <EyeOff size={20} className="text-[#E60000]" />
          <div className="flex-1">
            <div className="font-semibold">Disguise Mode (Calculator)</div>
            <div className="text-xs text-zinc-500">Type your unlock code in the calculator to return.</div>
          </div>
          <input type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)} data-testid="disguise-toggle" />
        </div>
        <input value={code} onChange={e=>setCode(e.target.value)} placeholder="Unlock code (e.g. =blink)" data-testid="disguise-code" className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3" />
        <button onClick={save} data-testid="privacy-save" className="mt-3 w-full bg-[#E60000] hover:bg-[#FF1A1A] py-3 rounded-full font-semibold transition">Save</button>
        <Link to="/disguise/calculator" data-testid="open-calculator" className="mt-3 block text-center text-sm font-mono-accent text-[#E60000]">Open calculator shell →</Link>
      </div>

      <div className="mt-6 glass rounded-2xl p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Lock size={16}/> Blocked users</h3>
        {blocks.length === 0 && <p className="text-zinc-500 text-sm">No one blocked.</p>}
        {blocks.map(b => (
          <div key={b.id} className="flex justify-between items-center py-2" data-testid={`block-${b.id}`}>
            <span>{b.display_name || "user"}</span>
            <button onClick={()=>unblock(b.id)} data-testid={`unblock-${b.id}`} className="text-xs font-mono-accent text-[#E60000]">UNBLOCK</button>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
        <p>• Location resolves only to a venue ID. Raw lat/lng never stored.</p>
        <p>• Real name + photo revealed only on mutual Keep.</p>
        <p>• Vibes, presence, chats expire when you leave or after 12h.</p>
        <p>• Blocks are absolute and one-directional.</p>
      </div>
    </Shell>
  );
}
